import { useEffect } from "react"

import { App, Flex, Modal, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import { modalConfig } from "../../theme"
import { useQuery } from "../../Utils/utils"
import StripeTokenModal from "./StripeTokenHelpModal"
import StripeTokenInvalidModal from "./StripeTokenInvalidModal"

const { Text } = Typography

const modalContent = (t) => ({
  stripe_token: {
    typeModal: "confirm",
    text: (
      <Flex vertical={true} gap={4}>
        <span>
          {t(
            "It looks like the Stripe account you're trying to connect is already linked to another Business Account."
          )}
        </span>
        <span>
          {t(
            "Unfortunately, you cannot connect the same Stripe account to multiple Chargeback Business Accounts."
          )}
        </span>
        <span>
          <Text strong>{t("Need help?")}</Text>{" "}
          {t("Contact Chargeback Support for assistance with connection issues.")}
        </span>
      </Flex>
    ),
    okText: t("Need help"),
    callIntercom: true
  },
  shopify_token: {
    typeModal: "confirm",
    text: (
      <Flex vertical={true} gap={4}>
        <span>
          {t(
            "It looks like the Shopify account you're trying to connect is already linked to another Business Account."
          )}
        </span>
        <span>
          {t(
            "Unfortunately, you cannot connect the same Shopify account to multiple Chargeback Business Accounts."
          )}
        </span>
        <span>
          <Text strong>{t("Need help?")}</Text>{" "}
          {t("Contact Chargeback Support for assistance with connection issues.")}
        </span>
      </Flex>
    ),
    okText: t("Need help"),
    callIntercom: true
  },
  nextcommerce_token: {
    typeModal: "confirm",
    text: (
      <Flex vertical={true} gap={4}>
        <span>
          {t(
            "It looks like the NextCommerce account you're trying to connect is already linked to another Business Account."
          )}
        </span>
        <span>
          {t(
            "Unfortunately, you cannot connect the same NextCommerce account to multiple Chargeback Business Accounts."
          )}
        </span>
        <span>
          <Text strong>{t("Need help?")}</Text>{" "}
          {t("Contact Chargeback Support for assistance with connection issues.")}
        </span>
      </Flex>
    ),
    okText: t("Need help"),
    callIntercom: true
  },
  slack_name_taken: {
    typeModal: "confirm",
    text: (
      <Flex vertical={true} gap={4}>
        <span>
          {t("The Slack channel name you entered already exists in the selected workspace.")}
        </span>
        <span>
          {t(
            "To continue, restart the Slack integration process and provide a unique channel name for this workspace."
          )}
        </span>
        <span>
          <Text strong>{t("Need help?")}</Text>{" "}
          {t("Contact Chargeback Support for assistance with connection issues.")}
        </span>
      </Flex>
    ),
    okText: t("Need help"),
    callIntercom: true
  },
  invite_removed: {
    typeModal: "info",
    text: t(
      "The access to the business account has been changed. Contact the owner of the account"
    ),
    okText: null
  },
  expired_invite: {
    typeModal: "info",
    text: t("The link has expired. Please contact the inviter"),
    okText: null
  },
  export_case_3: {
    typeModal: "info",
    title: t("Access Denied"),
    text: (
      <Flex vertical={true} gap={4}>
        <span>
          {t(
            "You no longer have access to this business account, so the file cannot be downloaded."
          )}
        </span>
        <span>{t("Please contact the account owner or your administrator.")}</span>
      </Flex>
    ),
    okText: t("Cancel")
  },
  export_case_4: {
    typeModal: "info",
    title: t("Access Denied"),
    text: (
      <Flex vertical={true} gap={4}>
        <span>
          {t("You no longer have access to this account, so the file cannot be downloaded.")}
        </span>
        <span>{t("Please contact the support team.")}</span>
      </Flex>
    ),
    okText: t("Cancel")
  },
  complete_ba_onboarding: {
    typeModal: "info",
    title: t("Complete the business account creation"),
    text: t("Please complete the business account creation before accepting the invite."),
    okText: t("Understood"),
    iconType: "info"
  }
})

const getModalConfig = ({ helpModal, helpTitle, helpText, isMobile, navigate, pathname, t }) => {
  const presetModalContent = helpModal ? modalContent(t)?.[helpModal] : {}

  const iconType = presetModalContent?.iconType || "exclamation"

  return {
    ...modalConfig(iconType),
    centered: !isMobile,
    title: presetModalContent?.title || helpTitle,
    content: presetModalContent?.text || helpText,
    okText: presetModalContent?.okText,
    okButtonProps: {
      ...modalConfig(iconType)?.okButtonProps
    },
    onCancel() {
      navigate(pathname, { replace: true })
    },
    onOk() {
      navigate(pathname, { replace: true })
      if (presetModalContent?.callIntercom) window?.Intercom?.("showNewMessage")
    }
  }
}

const useHelpQuery = (search) => {
  const query = useQuery(search)
  return {
    helpModal: query.get("help_modal"), //stripe_token
    helpTitle: query.get("help_title"),
    helpText: query.get("help_text")
  }
}

const customHelpModalsNames = ["stripe_token_error", "stripe_token_invalid"]

const customHelpModals = {
  stripe_token_error: <StripeTokenModal />,
  stripe_token_invalid: <StripeTokenInvalidModal />
}

export const HelpModal = () => {
  const isMobile = useSelector((state) => state.nav.isMobile)
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const { helpModal, helpTitle, helpText } = useHelpQuery(search)

  const isCustomModal = customHelpModalsNames.find((name) => name === helpModal)

  useEffect(() => {
    if (isCustomModal) return
    if (helpModal || helpTitle || helpText) {
      let config = getModalConfig({
        helpModal,
        helpTitle,
        helpText,
        isMobile,
        navigate,
        pathname,
        t
      })
      let typeModal = modalContent(t)?.[helpModal]?.typeModal || "info"

      if (typeModal === "confirm") {
        modal.confirm(config)
      } else {
        modal.info(config)
      }
    }

    return () => {
      if (helpModal || helpTitle || helpText) Modal?.destroyAll()
    }
  }, [helpModal, helpTitle, helpText, isMobile, pathname])

  if (isCustomModal) return customHelpModals[helpModal]

  return null
}
